import axios from 'axios'
import { useQuery, UseQueryResult } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { resolveZervantDocument } from '../query-api'
import { ResolvedZervantDocument } from '../types/resolvedZervantDocument'

// Billy ids are URL-safe strings. Anything else in the URL is not an id rest-invoice can resolve,
// and must never reach the request path.
const BILLY_ID_PATTERN = /^[\w-]+$/

const isBillyId = (value: unknown): value is string => typeof value === 'string' && BILLY_ID_PATTERN.test(value)

const isNotFoundError = (error: unknown): boolean => axios.isAxiosError(error) && error.response?.status === 404

type ResolveZervantDocumentQueryResult = UseQueryResult<Awaited<ReturnType<typeof resolveZervantDocument>> | null>

interface UseResolveZervantDocumentResult extends Omit<ResolveZervantDocumentQueryResult, 'data'> {
  document?: ResolvedZervantDocument
  isNotFound: boolean
}

/**
 * A 404 is an answer, not a failure: the query resolves it to `null` and reports `isNotFound`, so
 * it is neither retried nor reported. A malformed id is reported the same way without sending a
 * request, so `isNotFound` covers both a 404 and a malformed id.
 *
 * Every error is forwarded to `trackError` by the react-query logger, so callers don't report
 * errors themselves.
 */
export const useResolveZervantDocument = (billyId?: string): UseResolveZervantDocumentResult => {
  // apiZervant only gets the `organizations/:id` prefix once the organization has loaded.
  const { organization } = useUserOrganization()
  const organizationId = organization?.id
  const isIdValid = isBillyId(billyId)

  const { data: documentResponse, ...rest } = useQuery(
    [QueryKeys.ZervantDocumentResolve, organizationId, billyId],
    async () => {
      if (!isBillyId(billyId)) {
        return null
      }

      try {
        return await resolveZervantDocument(billyId)
      } catch (resolveError) {
        if (isNotFoundError(resolveError)) {
          return null
        }

        throw resolveError
      }
    },
    {
      enabled: !!organizationId && isIdValid,
      staleTime: defaultStaleTime,
    },
  )

  const document = documentResponse?.data
  const isNotFound = documentResponse === null || !isIdValid

  return { ...rest, document, isNotFound }
}
